import Head from 'next/head';

import { breakpoints } from 'styles/theme/breakpoints';
import { buildPTProductTypeLink } from 'productTemplate/utils/buildPTProductTypeLink';
import { FeatureFlags } from 'featureFlags/constants';
import { findParentProductCategory } from 'productTemplate/utils/findParentProductCategory';
import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { getAuctionPTEventSchema } from 'productTemplate/utils/getAuctionPTEventSchema';
import { getBreadcrumbSchema } from 'productTemplate/utils/getBreadcrumbSchema';
import { getEventSchema } from 'productTemplate/utils/getEventSchema';
import { getImageSrcs, IImageSrcs } from 'shared/utils/getImageSrcs';
import { getProductGroupTemplateSchema } from 'productTemplate/utils/getProductGroupTemplateSchema';
import { getProductTemplateCarouselImages } from 'productTemplate/utils/getProductTemplateCarouselImages';
import { getProductTemplateSchema } from 'productTemplate/utils/getProductTemplateSchema';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { isFutureReleaseDate } from 'product-landing-pages/utils/isFutureReleaseDate';
import { LOCALES } from 'localization/constants/configEnums';
import { t } from 'localization/utils/t';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { normalizeValue } from 'shared/utils/normalizeValue';

const TP = 'productTemplate.components.ProductDetailPageLanding';

interface IProductDetailPageHead {
  locale: LOCALES;
  offers?: IGmcOffers;
  productTemplate: IProductTemplate;
  productType: string;
}

export const ProductDetailPageHead = ({
  locale,
  offers,
  productTemplate,
  productType,
}: IProductDetailPageHead) => {
  const {
    brandName,
    color: productColor,
    forAuction: isAuctionPT,
    internalShot,
    isFashionProduct,
    mainPictureUrl,
    name: productName,
    productTemplateExternalPictures,
    productType: productTemplateProductType,
    releaseDate,
    silhouette,
    slug,
    status,
  } = productTemplate || {};
  const { pageTitle, pageDescription } = usePageMetadata({
    productTemplate,
    defaultTitle: `${productName || slug} `,
  });
  const productCategory = findParentProductCategory(productType);
  const isIgnoreInternalShotEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PDP_IGNORE_INTERNAL_SHOT,
  );
  const isProductGroupSchemaEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PRODUCT_GROUP_SCHEMA,
  );
  const carouselImages = getProductTemplateCarouselImages({
    productTemplate: { internalShot, mainPictureUrl, productTemplateExternalPictures },
    tempWebEnablePdpIgnoreInternalShot: isIgnoreInternalShotEnabled,
  });
  const firstImage = carouselImages[0];
  const isMissingMainPictureUrl = /missing.png/.test(firstImage || '');
  let imageSrcs: IImageSrcs = { src: '', srcSet: '' };
  if (!isMissingMainPictureUrl) {
    imageSrcs = getImageSrcs({
      crop: !(productTemplateProductType === 'tops' && productColor?.toLowerCase() === 'white'),
      src: firstImage?.mainPictureUrl?.replace('/medium/', '/original/'), // make sure the image source is same one in Image.tsx
      resizeable: true,
    });
  }
  const normalizedBrandValue = formatStringLowercaseWithDashes(normalizeValue(brandName));

  const fashionSchema = [
    {
      id: `/brand/${normalizedBrandValue}`,
      name: brandName,
    },
    {
      id: `/${productCategory || 'apparel'}/brand/${normalizedBrandValue}`,
      name: brandName
        ? t(`${TP}.fashionSchemaApparel`, '{brandName} Apparel', { brandName })
        : null,
    },
    {
      id: buildPTProductTypeLink(productCategory, productTemplateProductType, normalizedBrandValue),
      name: productType,
    },
  ];

  const sneakerSchema = [
    {
      id: `/brand/${normalizedBrandValue}`,
      name: brandName,
    },
    {
      id: `/sneakers/brand/${normalizedBrandValue}`,
      name: brandName
        ? t(`${TP}.sneakerSchemaSneakers`, '{brandName} Sneakers', { brandName })
        : null,
    },
    {
      id: `/sneakers/silhouette/${formatStringLowercaseWithDashes(normalizeValue(silhouette))}`,
      name: silhouette,
    },
  ];

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta property="og:title" content={pageTitle} key="og:title" />
      <>
        <meta name="description" content={pageDescription} key="description" />
        <meta property="og:description" content={pageDescription} key="og:description" />
      </>
      {status === 'inactive' && <meta name="robots" content="noindex" />}
      {offers && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: isProductGroupSchemaEnabled
              ? getProductGroupTemplateSchema(productTemplate, offers?.offerData)
              : getProductTemplateSchema(productTemplate, offers?.offerData),
          }}
        />
      )}
      {productTemplateProductType === 'sneakers' && isFutureReleaseDate(releaseDate) && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getEventSchema(productTemplate, locale),
          }}
        />
      )}

      {isAuctionPT && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getAuctionPTEventSchema(productTemplate),
          }}
        />
      )}

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: getBreadcrumbSchema(isFashionProduct ? fashionSchema : sneakerSchema, locale),
        }}
      />

      {!isMissingMainPictureUrl && imageSrcs.src && (
        <link
          rel="preload"
          as="image"
          href={imageSrcs.src}
          imageSrcSet={imageSrcs.srcSet}
          imageSizes={`(max-width: ${breakpoints.mediumSmall}px) 50vw, (max-width: ${breakpoints.mediumLarge}px) 350px, 30vw`} /* eslint-disable-line react/no-unknown-property */
          type="image/webp"
          fetchpriority="high" /* eslint-disable-line react/no-unknown-property */
        />
      )}
    </Head>
  );
};
